/**
 * The plate calculator's arithmetic (`D209`, B3) — PURE.
 *
 * ── PLATES PER SIDE, AS FEW AS POSSIBLE, AND THE NEAREST WHEN NOT EXACT ────
 * A barbell is loaded symmetrically, so a target is the bar plus twice one
 * side. The side is found by an exact search over the plates the person has
 * (unlimited pairs of each size), fewest plates first — greedy would miss a
 * load like 2 × 15 that a set with no 30 can still make. When the target
 * cannot be loaded exactly, the answer is the NEAREST load that can (a tie
 * goes to the lighter one, which is the safer mistake under a bar), and the
 * sheet says "mais próximo: X kg" rather than pretending.
 *
 * Kilograms are worked in integer hundredths, so 1.25 kg plates add up
 * without float dust; the search runs in steps of the plates' greatest common
 * divisor, which keeps it to a few hundred cells for any real bar.
 *
 * ── POUNDS ARE THE SAME ARITHMETIC OVER A POUND GYM (`D266`) ───────────────
 * Nothing in the search knows a unit: hundredths of a pound add up exactly as
 * hundredths of a kilogram do. A person who reads pounds trains in a gym
 * whose plates ARE pounds — 45, 35, 25, 10, 5, 2.5 — on a 45 lb bar, so the
 * sheet runs {@link platesFor} over {@link LB_PLATE_SIZES} and {@link LB_BARS}
 * with the target converted to pounds, and converts only the answer back to
 * kilograms for the dial. Converting kilogram plates to pounds instead would
 * show "11,02 lb" discs that exist in no gym.
 */

/** The plates a gym has, heaviest first, when the person has said nothing. */
export const DEFAULT_PLATES = [25, 20, 15, 10, 5, 2.5, 1.25] as const;

/** The sizes the sheet offers to switch on or off. */
export const PLATE_SIZES: readonly number[] = DEFAULT_PLATES;

/** The bars the sheet offers, and what each weighs. "EZ ~8" is a typical EZ. */
export const BARS = {
  ez: 8,
  olympic: 20,
  technique: 10,
  women: 15,
} as const;

export type BarKind = keyof typeof BARS | "custom";

/** A pound gym's plates, heaviest first, when the person has said nothing. */
export const DEFAULT_LB_PLATES = [45, 35, 25, 10, 5, 2.5] as const;

/** The pound sizes the sheet offers: the defaults plus 55 and 1.25. */
export const LB_PLATE_SIZES: readonly number[] = [
  55, 45, 35, 25, 10, 5, 2.5, 1.25,
];

/**
 * The same four bars in a pound gym: the 45 lb men's bar, the 35 lb women's,
 * a 15 lb technique bar and a typical 25 lb EZ.
 */
export const LB_BARS = {
  ez: 25,
  olympic: 45,
  technique: 15,
  women: 35,
} as const;

export interface PlateLoad {
  /** One side's plates, heaviest first. Empty is the bar alone. */
  readonly perSide: readonly number[];
  /** Bar + both sides, in kilograms. */
  readonly totalKg: number;
}

export interface PlateAnswer {
  /** `true` when `load.totalKg` IS the target. */
  readonly exact: boolean;
  readonly load: PlateLoad;
}

const UNIT = 100;

const toUnits = (kg: number) => Math.round(kg * UNIT);
const toKg = (units: number) => units / UNIT;

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

/**
 * Fewest plates per side for every reachable side weight up to `limit`
 * (units), as `best[w] = plate count` and `last[w] = the plate added`.
 */
function sideTable(plates: readonly number[], limit: number, step: number) {
  const cells = Math.floor(limit / step) + 1;
  const best = new Array<number>(cells).fill(Number.POSITIVE_INFINITY);
  const last = new Array<number>(cells).fill(0);
  best[0] = 0;
  for (let cell = 1; cell < cells; cell += 1) {
    for (const plate of plates) {
      const span = plate / step;
      const from = cell - span;
      if (
        from >= 0 &&
        (best[from] ?? Number.POSITIVE_INFINITY) + 1 <
          (best[cell] ?? Number.POSITIVE_INFINITY)
      ) {
        best[cell] = (best[from] ?? 0) + 1;
        last[cell] = plate;
      }
    }
  }
  return { best, last };
}

function platesOf(
  last: readonly number[],
  cell: number,
  step: number
): number[] {
  const out: number[] = [];
  let at = cell;
  while (at > 0) {
    const plate = last[at] ?? 0;
    if (plate === 0) {
      break;
    }
    out.push(toKg(plate));
    at -= plate / step;
  }
  return out.sort((a, b) => b - a);
}

/**
 * How to load `targetKg` on a bar of `barKg` with `plates` (kilograms, any
 * order, unlimited pairs), or the nearest load that can be made.
 *
 * A target at or under the bar is the bar alone — exact only when equal.
 * With no plates at all, the bar is the only load there is.
 */
export function platesFor(
  targetKg: number,
  barKg: number,
  plates: readonly number[]
): PlateAnswer {
  const bar = toUnits(barKg);
  const target = toUnits(targetKg);
  const sizes = [...new Set(plates.map(toUnits).filter((p) => p > 0))];
  const alone: PlateAnswer = {
    exact: target === bar,
    load: { perSide: [], totalKg: toKg(bar) },
  };
  if (target <= bar || sizes.length === 0) {
    return alone;
  }
  const step = sizes.reduce((g, p) => gcd(g, p), 0);
  const perSide = (target - bar) / 2;
  const heaviest = Math.max(...sizes);
  // Far enough past the target that the nearest load ABOVE it is in reach.
  const limit = Math.ceil((perSide + heaviest) / step) * step;
  const { best, last } = sideTable(sizes, limit, step);
  let chosen = 0;
  let distance = Number.POSITIVE_INFINITY;
  for (let cell = 0; cell * step <= limit; cell += 1) {
    if (!Number.isFinite(best[cell] ?? Number.POSITIVE_INFINITY)) {
      continue;
    }
    const gap = Math.abs(cell * step - perSide);
    // Strictly nearer wins; on a tie the lighter (earlier) cell stays.
    if (gap < distance) {
      distance = gap;
      chosen = cell;
    }
  }
  const side = platesOf(last, chosen, step);
  const totalKg = toKg(bar + 2 * chosen * step);
  return {
    exact: toUnits(totalKg) === target,
    load: { perSide: side, totalKg },
  };
}

/** The nearest load `platesFor` can make — what a warm-up rounds to. */
export function nearestLoadable(
  targetKg: number,
  barKg: number,
  plates: readonly number[]
): number {
  return platesFor(targetKg, barKg, plates).load.totalKg;
}

/**
 * What a bar weighs in the given unit: one of {@link BARS} (kilograms) or
 * {@link LB_BARS} (pounds), or the person's own number, already in that unit.
 */
export function barWeightOf(
  kind: BarKind,
  custom: number,
  unit: "kg" | "lb" = "kg"
): number {
  if (kind === "custom") {
    return custom;
  }
  return unit === "lb" ? LB_BARS[kind] : BARS[kind];
}
